import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { normaliseBooksResponse, getStatus } from "../utils/books";
import { useAuth } from "../AuthContext";

function Profile() {
  const navigate = useNavigate();
  const { setIsLoggedIn } = useAuth();
  const userId = localStorage.getItem("userId");

  const [user,    setUser]    = useState(null);
  const [books,   setBooks]   = useState([]);
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({ firstName: '', lastName: '', city: '' });
  const [saving,  setSaving]  = useState(false);

  useEffect(() => {
    if (!userId) { navigate('/login'); return; }
    axios.get(`/api/user/details?email=${userId}`)
      .then(r => { setUser(r.data); setForm({ firstName: r.data.firstName, lastName: r.data.lastName, city: r.data.city || '' }); })
      .catch(() => {});
    axios.get(`/api/books/${userId}`)
      .then(r => setBooks(normaliseBooksResponse(r.data)))
      .catch(() => {});
  }, [userId, navigate]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`/api/user/update?email=${userId}`, form);
      setUser(u => ({ ...u, ...form }));
      localStorage.setItem('firstName', form.firstName);
      localStorage.setItem('lastName',  form.lastName);
      setEditing(false);
    } finally { setSaving(false); }
  };

  const handleLogout = () => {
    ['userId', 'encryptedUserId', 'authToken', 'firstName', 'lastName'].forEach(k => localStorage.removeItem(k));
    setIsLoggedIn(false);
    navigate('/');
  };

  const finished  = books.filter(b => getStatus(b) === 'finished').length;
  const reading   = books.filter(b => getStatus(b) === 'reading').length;
  const wishlist  = books.filter(b => getStatus(b) === 'wishlist').length;
  const thisYear  = new Date().getFullYear();
  const doneThisYear = books.filter(b => {
    if (getStatus(b) !== 'finished') return false;
    const d = b.finishDate || b.addedOn;
    return d && new Date(d).getFullYear() === thisYear;
  }).length;

  const initials = user
    ? `${(user.firstName || '?')[0]}${(user.lastName || '')[0] || ''}`.toUpperCase()
    : '?';

  if (!user) return (
    <div className="min-h-screen bg-ink-base flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-ink-base">
      <div className="max-w-lg mx-auto px-5 sm:px-8 py-10 space-y-8">

        {/* Avatar + name */}
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-full bg-ink-raised border border-ink-border flex items-center justify-center flex-shrink-0">
            <span className="text-xl font-bold text-cream tracking-tight">{initials}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-cream text-lg font-bold leading-tight">
              {user.firstName} {user.lastName}
            </p>
            <p className="text-cream-muted text-sm">{user.email}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 gap-px bg-ink-border rounded-xl overflow-hidden">
          {[
            { value: finished,    label: 'books read'   },
            { value: doneThisYear, label: `in ${thisYear}` },
            { value: reading,     label: 'reading now'  },
            { value: wishlist,    label: 'want to read' },
          ].map(s => (
            <div key={s.label} className="bg-ink-surface px-5 py-4">
              <p className="text-2xl font-bold text-cream tabular-nums">{s.value}</p>
              <p className="text-cream-muted text-xs mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Account details */}
        <div className="bg-ink-surface border border-ink-border rounded-xl overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-ink-border">
            <p className="text-cream text-sm font-semibold">Account</p>
            {!editing && (
              <button onClick={() => setEditing(true)}
                className="text-xs text-cream-muted hover:text-cream transition-colors">
                Edit
              </button>
            )}
          </div>

          {editing ? (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-cream-muted text-xs font-medium block mb-1.5">First name</label>
                  <input className="sc-input text-sm" value={form.firstName}
                    onChange={e => setForm(f => ({ ...f, firstName: e.target.value }))} />
                </div>
                <div>
                  <label className="text-cream-muted text-xs font-medium block mb-1.5">Last name</label>
                  <input className="sc-input text-sm" value={form.lastName}
                    onChange={e => setForm(f => ({ ...f, lastName: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className="text-cream-muted text-xs font-medium block mb-1.5">City</label>
                <input className="sc-input text-sm" value={form.city}
                  onChange={e => setForm(f => ({ ...f, city: e.target.value }))} />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={handleSave} disabled={saving}
                  className="sc-btn-gold text-xs py-1.5 px-4 disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                <button onClick={() => setEditing(false)} className="sc-btn-ghost text-xs py-1.5 px-4">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-ink-border">
              {[
                { label: 'First name', value: user.firstName },
                { label: 'Last name',  value: user.lastName  },
                { label: 'City',       value: user.city || '—' },
                { label: 'Email',      value: user.email },
              ].map(r => (
                <div key={r.label} className="flex items-center justify-between px-5 py-3.5">
                  <span className="text-cream-muted text-sm">{r.label}</span>
                  <span className="text-cream text-sm">{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="bg-ink-surface border border-ink-border rounded-xl overflow-hidden divide-y divide-ink-border">
          {[
            { to: '/addbook',    label: 'Dashboard' },
            { to: '/library',    label: 'Library'   },
            { to: '/journal',    label: 'Journal'   },
            { to: '/collections',label: 'Collections'},
          ].map(l => (
            <Link key={l.to} to={l.to}
              className="flex items-center justify-between px-5 py-3.5 hover:bg-ink-raised transition-colors">
              <span className="text-cream text-sm">{l.label}</span>
              <span className="text-cream-muted text-xs">→</span>
            </Link>
          ))}
        </div>

        {/* Sign out */}
        <button onClick={handleLogout}
          className="w-full text-center text-sm text-cream-muted hover:text-crimson transition-colors py-2">
          Sign out
        </button>
      </div>
    </div>
  );
}

export default Profile;
